import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { getActiveEscalations, getEscalationStats, takeEscalationAction, dismissEscalation } from '../lib/api';
import { toast } from 'sonner';
import { useWebSocket } from '../contexts/WebSocketContext';
import {
  AlertTriangle,
  DollarSign,
  FileText,
  Check,
  X,
  Clock,
  ChevronRight,
  Bell,
  Loader2,
} from 'lucide-react';

export const EscalationAlerts = ({ onRefresh }) => {
  const navigate = useNavigate();
  const [escalations, setEscalations] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    loadData();
    // Refresh every 30 seconds
    const interval = setInterval(loadData, 30000);
    return () => clearInterval(interval);
  }, []);

  // Real-time escalation updates
  const ws = useWebSocket();
  useEffect(() => {
    if (!ws) return;
    const events = ['escalation_action', 'deal_funded', 'deal_updated', 'deal_created'];
    const unsubs = events.map(evt => ws.subscribe(evt, () => loadData()));
    return () => unsubs.forEach(fn => fn());
  }, [ws]);

  const loadData = async () => {
    try {
      const [escRes, statsRes] = await Promise.all([
        getActiveEscalations(),
        getEscalationStats(),
      ]);
      setEscalations(escRes.data);
      setStats(statsRes.data);
    } catch (err) {
      console.error('Failed to load escalations', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async (escalationId, action, notes = null) => {
    setActionLoading(escalationId);
    try {
      await takeEscalationAction(escalationId, action, notes);
      toast.success(
        action === 'fund' ? 'Deal approved for funding!' :
        action === 'request_stips' ? 'Stips request sent!' :
        action === 'acknowledge' ? 'Escalation acknowledged' :
        'Action completed'
      );
      loadData();
      if (onRefresh) onRefresh();
    } catch (err) {
      toast.error('Action failed');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDismiss = async (escalationId) => {
    try {
      await dismissEscalation(escalationId);
      toast.success('Escalation dismissed');
      loadData();
    } catch (err) {
      toast.error('Failed to dismiss');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
    }).format(amount || 0);
  };

  if (loading) {
    return null;
  }

  if (escalations.length === 0) {
    return null;
  }

  return (
    <div className="space-y-4" data-testid="escalation-alerts">
      {/* Escalation Summary Bar */}
      <div className="bg-red-600 text-white p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <AlertTriangle size={24} className="animate-pulse" />
              <span className="font-heading text-lg font-bold uppercase tracking-wider">
                Active Escalations
              </span>
            </div>
            <div className="flex items-center gap-6 ml-6">
              <div className="flex items-center gap-2 bg-red-800 px-3 py-1">
                <span className="font-mono font-bold">{stats?.total_deals_10plus || 0}</span>
                <span className="text-sm">Deals {stats?.critical_threshold || 10}+ Days</span>
              </div>
              <div className="flex items-center gap-2">
                <DollarSign size={16} />
                <span className="font-mono font-bold">{formatCurrency(stats?.total_cash_at_risk)}</span>
                <span className="text-sm opacity-80">At Risk</span>
              </div>
            </div>
          </div>
          <span className="text-xs opacity-70">
            Auto-checks every 5 minutes
          </span>
        </div>
      </div>

      {/* Escalation Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
        {(showAll ? escalations : escalations.slice(0, 6)).map((esc) => (
          <div
            key={esc.id}
            className={`bg-white border-l-4 p-4 ${
              esc.escalation_level === 'urgent' ? 'border-l-red-600 bg-red-50' :
              esc.escalation_level === 'critical' ? 'border-l-amber-500 bg-amber-50' :
              'border-l-yellow-400'
            }`}
            data-testid={`escalation-card-${esc.id}`}
          >
            {/* Header */}
            <div className="flex items-start justify-between mb-3">
              <div>
                <div className="flex items-center gap-2">
                  <span className={`px-2 py-0.5 text-xs font-bold uppercase ${
                    esc.escalation_level === 'urgent' ? 'bg-red-600 text-white' :
                    esc.escalation_level === 'critical' ? 'bg-amber-500 text-white' :
                    'bg-yellow-400 text-yellow-900'
                  }`}>
                    {esc.escalation_level}
                  </span>
                  <span className="text-xs text-slate-500 font-mono">#{esc.deal_number}</span>
                </div>
                <h4 className="font-medium text-slate-900 mt-1">{esc.customer_name}</h4>
              </div>
              <button
                onClick={() => handleDismiss(esc.id)}
                className="text-slate-400 hover:text-slate-600"
                title="Dismiss"
              >
                <X size={16} />
              </button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-2 mb-3">
              <div className="text-center p-2 bg-white border border-slate-200">
                <p className={`font-mono font-bold ${esc.deal_age >= 10 ? 'text-red-600' : 'text-amber-600'}`}>
                  {esc.deal_age}
                </p>
                <p className="text-xs text-slate-500">Days</p>
              </div>
              <div className="text-center p-2 bg-white border border-slate-200">
                <p className="font-mono font-bold text-red-600">{esc.missing_stips}</p>
                <p className="text-xs text-slate-500">Stips</p>
              </div>
              <div className="text-center p-2 bg-white border border-slate-200">
                <p className="font-mono font-bold text-slate-900 text-sm">{formatCurrency(esc.cash_at_risk)}</p>
                <p className="text-xs text-slate-500">At Risk</p>
              </div>
            </div>

            {/* Info */}
            <div className="text-xs text-slate-500 mb-3">
              <p><span className="font-semibold">Lender:</span> {esc.lender}</p>
              <p><span className="font-semibold">FM:</span> {esc.finance_manager}</p>
              {esc.status === 'acknowledged' && (
                <p className="text-amber-600 mt-1">
                  <Clock size={12} className="inline mr-1" />
                  Acknowledged by {esc.acknowledged_by}
                </p>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex gap-2">
              {esc.status === 'pending' && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleAction(esc.id, 'acknowledge')}
                  disabled={actionLoading === esc.id}
                  className="flex-1 text-xs"
                >
                  <Clock size={14} className="mr-1" />
                  Acknowledge
                </Button>
              )}
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleAction(esc.id, 'request_stips', 'All missing documents')}
                disabled={actionLoading === esc.id}
                className="flex-1 text-xs"
                data-testid={`request-stips-${esc.id}`}
              >
                {actionLoading === esc.id ? (
                  <Loader2 size={14} className="mr-1 animate-spin" />
                ) : (
                  <FileText size={14} className="mr-1" />
                )}
                Request Stips
              </Button>
              <Button
                size="sm"
                onClick={() => handleAction(esc.id, 'fund')}
                disabled={actionLoading === esc.id}
                className="flex-1 text-xs bg-emerald-600 hover:bg-emerald-700"
                data-testid={`fund-deal-${esc.id}`}
              >
                {actionLoading === esc.id ? (
                  <Loader2 size={14} className="mr-1 animate-spin" />
                ) : (
                  <Check size={14} className="mr-1" />
                )}
                Fund Deal
              </Button>
            </div>

            {/* View Deal Link */}
            <button
              onClick={() => navigate(`/deal/${esc.deal_id}`)}
              className="w-full mt-2 text-xs text-slate-500 hover:text-slate-700 flex items-center justify-center gap-1"
            >
              View Full Deal <ChevronRight size={12} />
            </button>
          </div>
        ))}
      </div>

      {/* Show more / Show less toggle */}
      {escalations.length > 6 && (
        <div className="text-center">
          <button
            onClick={() => setShowAll(!showAll)}
            className="text-sm text-slate-600 hover:text-slate-900 underline"
            data-testid="toggle-all-escalations"
          >
            {showAll ? 'Show fewer' : `View all ${escalations.length} escalations`}
          </button>
        </div>
      )}
    </div>
  );
};
